import React, { useState, Fragment, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { format } from "date-fns";
import Tooltip from "../tooltip";
import UploadFileIcon from "../../icons/uploadFileIcon";
import CameraIcon from "../../icons/cameraIcon";
import CloseIcon from "../../icons/closeIcon";
import CheckThinIcon from "../../icons/checkThin";
import ResetIcon from "../../icons/resetIcon";
import LoopIcon from "../../icons/loopIcon";
import useMediaQuery from "../../hooks/useMediaQuery";
import styles from "./style.module.css";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const FileUpload = (props) => {
  const isMobile = useMediaQuery("(max-width: 767.98px)");
  const { required, onChange, id, readonly, value, label, options } = props;
  const {
    tooltipText,
    position,
    transform,
    customClass,
  } = options;

  const [open, setOpen] = useState(false);
  const [mainFile, setMainFile] = useState(value);

  const handleImageUpload = async (event) => {
    var file = event.target.files[0];
    setMainFile(file);
    onChange(await toBase64(file));
    var reader = new FileReader();
    if (file) {
      setMainFile(file);
      reader.readAsDataURL(file);
    } else {
      setMainFile("Upload Attachment");
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheck = async (file) => {
    onChange(file);
    setMainFile({ name: "captured-" + format(new Date(), "dd-MM-yyyy-HH-mm-ss") + ".jpg" });
    setOpen(false);
  };

  return (
    <Fragment>
      {label && (
        <label className="control-label" htmlFor={id}>
          <b>{label}</b>
          {required && <span className="required">*</span>}
          {tooltipText && (
            <Tooltip
              content={tooltipText}
              position={position}
              transform={transform}
              customClass={`${customClass} ${styles.tooltip_icon}`}
            />
          )}
        </label>
      )}
      <div className={styles.fileInput}>
        <label className={styles.fileInput_label}>
          <strong>{mainFile ? mainFile.name : "Upload Attachment"}</strong>
          <UploadFileIcon />
          <input
            hidden
            readOnly={readonly}
            required={required}
            id={id}
            accept=".pdf,image/*"
            type="file"
            onChange={handleImageUpload}
          />
        </label>
        {isMobile && (
          <button type="button" className={styles.fileInput_button} onClick={handleOpen}>
            <CameraIcon />
          </button>
        )}
      </div>
      {isMobile && (
        <FileInputDialog open={open} onClose={handleClose} onCheck={handleCheck} />
      )}
    </Fragment>
  );
};
export default FileUpload;

const FileInputDialog = ({ open, onClose, onCheck }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasFile, setCanvasFile] = useState(null);
  const [enableBack, setEnableBack] = useState(true);

  const handleCapture = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!enableBack) {
      context.scale(-1, 1); // Flip horizontally to match video display
      context.translate(-canvas.width, 0);
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const file = canvas.toDataURL('image/jpeg');
    setCanvasFile(file);

    // Stop camera recording after capture
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleCheck = () => {
    setCanvasFile(null);
    if (canvasFile) {
      onCheck(canvasFile);
    }
  };

  const handleReset = () => {
    setCanvasFile(null);

    // Restart video stream after reset
    if (videoRef.current) {
      // Try with environment camera first
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: enableBack ? "environment" : "user" } }
      })
        .then(mediaStream => {
          videoRef.current.srcObject = mediaStream;
        })
        .catch(err => {
          console.warn("Error restarting camera with ideal constraint:", err);

          // Fallback to any available camera
          navigator.mediaDevices.getUserMedia({ video: true })
            .then(mediaStream => {
              videoRef.current.srcObject = mediaStream;
            })
            .catch(fallbackErr => {
              console.error("Error restarting any camera:", fallbackErr);
            });
        });
    }
  };

  const handleClose = () => {
    setCanvasFile(null);
    setEnableBack(false);
    onClose();

    // Stop camera recording after capture
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleToggleCamera = () => {
    setCanvasFile(null);
    setEnableBack((prevEnableBack) => !prevEnableBack);
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());

      // Try to determine current facing mode, but don't rely on it
      let currentFacingMode = "user";
      try {
        currentFacingMode = stream.getVideoTracks()[0].getSettings().facingMode || "user";
      } catch (err) {
        console.warn("Could not determine camera facing mode:", err);
      }

      const newFacingMode = currentFacingMode === "environment" ? "user" : "environment";

      // First try with ideal instead of exact to avoid OverconstrainedError
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacingMode } }
      })
        .then(mediaStream => {
          videoRef.current.srcObject = mediaStream;
        })
        .catch(err => {
          console.warn(`Error accessing ${newFacingMode} camera with ideal constraint:`, err);

          // If that fails, try with no specific facingMode
          navigator.mediaDevices.getUserMedia({ video: true })
            .then(mediaStream => {
              videoRef.current.srcObject = mediaStream;
              console.log("Using default camera as fallback");
            })
            .catch(fallbackErr => {
              console.error("Error accessing any camera:", fallbackErr);
            });
        });
    }
  };

  useEffect(() => {
    let stream;
    if (open) {
      if (!navigator?.mediaDevices?.getUserMedia) {
        console.error("Camera access is not supported by this browser");
        return;
      }

      // Check if we already have an active stream to avoid asking permission again
      if (videoRef.current?.srcObject) {
        return; // Use existing stream if available
      }

      setEnableBack(true);
      // First try with ideal instead of exact to avoid OverconstrainedError
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }
      })
        .then((mediaStream) => {
          stream = mediaStream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch(err => {
          console.error("Error accessing environment camera with ideal constraint:", err);

          // Try with no specific facingMode as fallback
          navigator.mediaDevices.getUserMedia({ video: true })
            .then((mediaStream) => {
              stream = mediaStream;
              if (videoRef.current) {
                videoRef.current.srcObject = stream;
              }
              console.log("Using default camera as fallback");
            })
            .catch(fallbackErr => {
              console.error("Error accessing any camera:", fallbackErr);
            });
        });
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className={styles.fileInput_dialog}>
      <div className={`${styles.fid_inner} ${canvasFile ? styles.fid_inner_active : ""} ${enableBack ? styles.fid_inner_back : ""}`}>
        <video
          ref={videoRef}
          width="320"
          height="240"
          autoPlay
          playsInline
        />
        <canvas ref={canvasRef} />
        <div className={styles.fid_button_container}>
          <button type="button" onClick={handleClose}>
            <CloseIcon />
          </button>
          {canvasFile ? (
            <Fragment>
              <button type="button" onClick={handleReset}>
                <ResetIcon />
              </button>
              <button type="button" onClick={handleCheck}>
                <CheckThinIcon />
              </button>
            </Fragment>
          ) :
            <Fragment>
              <button type="button" className={styles.fid_button} onClick={handleCapture}>
                <span />
              </button>
              <button type="button" onClick={handleToggleCamera}>
                <LoopIcon />
              </button>
            </Fragment>
          }
        </div>
      </div>
    </div>,
    document.body
  );
};
